// src/pages/builder/LessonVideoPanel.js
//
// LESSON VIDEO STUDIO — Layout Builder panel.
//
// Animated lesson videos: the SBL students, a teaching scene (the clock, or
// big words on screen) and the lyrics along the bottom, all following the
// song's sing-along timings.
//
// TWO PLACES TO ADJUST A VIDEO
// ---------------------------
//   * WHEN things happen → the Sing-along studio. The video uses the song's
//     line timings (lyricSync/{slug}); fix a line there and the video follows.
//   * WHAT happens on each line → the Storyboard editor in this panel. Every
//     lyric line of the song is listed; pick the scene for it. Saved to
//     lessonStoryboards/{slug}.
//
// A template (src/components/video/storyboards/) can fill in a first draft;
// for Telling Time it does. Anything it can't match shows as "same scene
// continues" for you to set.
//
// LYRIC PICTURES
// --------------
// A line's scene can be a PICTURE: one made for that lyric line by
// src/scripts/make-lyric-images.mjs (saved in lyricImages/{slug}), or any
// image URL. A song with pictures and no storyboard starts with every line
// on its picture; "Use lyric pictures" does the same for a saved one. Exact
// numbers and equations go in "Words over the picture", drawn by the video
// itself, because AI pictures are kept free of digits on purpose.
//
// Pictures can also be made right here: "✨ Make AI picture" on a line's
// Picture scene, or "✨ Make pictures for every line" above the storyboard.
// Both call the Image studio's generateImage Cloud Function (admins only) and
// record the result in lyricImages/{slug}, so a picture is kept even if the
// storyboard isn't saved.
//
// WHAT IT READS AND WRITES
// ------------------------
// Reads topicLibrary, lyricSync, lessonStoryboards, lyricImages. Writes, only
// on a button:
//   * lessonStoryboards/{slug}           — Save storyboard
//   * videos/{topic}{year}animated.mp4   — Save to SBL storage
//   * lessonVideo* fields on lyricSync    — (same button, remembers the video)
//   * topicLibrary/{id}.videoUrl          — Use the animated video
// Never portalLayouts.
//
// STYLING
// -------
// Reuses the Sing-along studio's scoped .sa-* classes under .sa-panel, plus
// .lv-* rules in components/video/lessonVideo.css.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import {
  attachTopicVideo,
  exactKey,
  fileKeyOf,
  getAllLibraryTopics,
  legacyKey,
  makeToolKey,
} from "../../services/topicLibraryService";
import {
  SBL_LOGOS,
  cleanCues,
  endAtOf,
  fadeLevelAt,
  fileFadesItself,
  formatTime,
  fullSongUrlOf,
  getAllLyricSyncs,
  mediaKey,
  parseLyrics,
  saveLyricSync,
  stopTimeOf,
  syncMatchesSong,
  syncSignature,
  toLyricLines,
} from "../../services/lyricSyncService";
import {
  getAllLessonStoryboards,
  getAllLyricImages,
  linePicturePrompt,
  lyricImageUrls,
  makeLinePicture,
  saveLessonStoryboard,
  storyboardSignature,
} from "../../services/lessonStoryboardService";
import {
  canExportVideo,
  exportSingAlongVideo,
  pickVideoMime,
} from "../../components/singalong/exportSingAlongVideo";
import ExportPreview from "../../components/singalong/ExportPreview";
import {
  buildTimeline,
  onPictureLoaded,
  pictureUrlsOf,
  preloadPictures,
  renderLessonFrame,
} from "../../components/video/lessonVideo";
import { makeSmoothClock } from "../../components/video/smoothClock";
import { CAST, CAST_ORDER, ACTIONS } from "../../components/video/characters";
import {
  STORYBOARDS,
  blankStoryboard,
  seedFromTemplate,
  sungLinesOf,
  templateByKey,
  templateForTopic,
} from "../../components/video/storyboards";
import { CAMERAS, cameraLabel, groupColor, lookDirections } from "../../components/video/artStyles";
import {
  GroupsBar,
  LineDot,
  PictureLook,
  StorylineBox,
  assignLineToGroup,
  groupIdOf,
  linesInGroup,
  normText,
} from "./LessonPictureTools";
import "../../components/singalong/singalong.css";
import "../../components/video/lessonVideo.css";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// Everything is laid out on a 1280×720 frame and scaled, so 1080p videos
// look the same as the preview, only sharper.
const BASE_W = 1280;
const BASE_H = 720;

const SIZES = {
  "720": { width: 1280, height: 720, label: "720p (faster upload)" },
  "1080": { width: 1920, height: 1080, label: "1080p (sharper)" },
};

/** motion -> motion2026animated.mp4 (same pattern as …song.mp3 / …singalong.mp4). */
const animatedFileName = (slug, ext = "mp4") =>
  `${fileKeyOf(slug)}${new Date().getFullYear()}animated.${String(ext).replace(/^\./, "")}`;

const fileNameOf = (url) => mediaKey(url).split("/").pop() || "";

const drawScaled = (ctx, width, height, frameOpts) => {
  ctx.save();
  ctx.setTransform(width / BASE_W, 0, 0, height / BASE_H, 0, 0);
  renderLessonFrame(ctx, { W: BASE_W, H: BASE_H, ...frameOpts });
  ctx.restore();
};

const videoSignature = (sync, sb) =>
  `${syncSignature({ lines: sync?.lines, cues: sync?.cues, endAt: sync?.endAt })}.${storyboardSignature(sb)}`;

/** Loads the SBL logo so the canvas can still be recorded (needs bucket CORS). */
const useLogo = () => {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => alive && setLogo(img);
    img.src = SBL_LOGOS.white;
    return () => {
      alive = false;
    };
  }, []);
  return logo;
};

const clone = (v) => JSON.parse(JSON.stringify(v ?? null));

const HOURS = Array.from({ length: 12 }, (_, n) => n + 1);
const MINUTES = Array.from({ length: 60 }, (_, n) => n);
const two = (n) => String(n).padStart(2, "0");

const NEW_CLOCK_SCENE = { scene: "clock", clock: { hour: 12, minute: 0 } };

/**
 * Puts every line that has a lyric picture on its picture. Lines without one
 * keep whatever they had. A line already on a picture keeps its words-over
 * and its layout; only the picture is swapped.
 */
const withLyricPictures = (sb, lines, pictureMap) => {
  const byLine = { ...(sb.byLine || {}) };
  let used = 0;
  sungLinesOf(lines).forEach(({ i }) => {
    const url = pictureMap[i];
    if (!url) return;
    const cur = byLine[i];
    const base = cur && !cur.keep ? clone(cur) : {};
    if (base.scene !== "picture") {
      delete base.clock;
      delete base.notchCount;
      delete base.fx;
      delete base.labels;
      delete base.readout;
      delete base.caption;
    }
    byLine[i] = { ...base, scene: "picture", picture: { ...(base.picture || {}), url } };
    used += 1;
  });
  return { sb: { ...sb, byLine }, used };
};

/* ------------------------------------------------------------------ */
/* Describing a scene in a few words (the collapsed row)               */
/* ------------------------------------------------------------------ */

const describe = (shot) => {
  if (!shot || shot.keep) return [{ tone: "grey", text: "same scene continues" }];
  const out = [];
  const fx = shot.fx || {};
  if (shot.scene === "picture") {
    out.push({ tone: shot.picture?.url ? "green" : "amber", text: shot.picture?.url ? "picture" : "picture — not made yet" });
    if (shot.picture?.idea) out.push({ tone: "blue", text: "💬 your directions" });
    if (shot.picture?.follow) out.push({ tone: "blue", text: "↪ builds on the line above" });
    if (shot.picture?.fit === "full") out.push({ tone: "grey", text: "full screen" });
    if (shot.picture?.camera) out.push({ tone: "grey", text: `🎥 ${cameraLabel(shot.picture.camera).replace(/ \(.*\)$/, "").toLowerCase()}` });
    if (shot.caption) out.push({ tone: "blue", text: `“${shot.caption}”` });
  } else if ((shot.scene || "clock") === "students") {
    out.push({ tone: "grey", text: "students only" });
    if (shot.caption) out.push({ tone: "blue", text: `“${shot.caption}”` });
  } else {
    if (shot.clock?.spin) out.push({ tone: "grey", text: "hands spin" });
    else if (shot.notchCount) out.push({ tone: "blue", text: `counts ${shot.clock?.hour ?? 12}:01 → :05` });
    else if (shot.clock) out.push({ tone: "grey", text: `${shot.clock.hour}:${two(shot.clock.minute)}` });
    if (fx.glowHour) out.push({ tone: "amber", text: "little hand glows" });
    if (fx.wobbleHour) out.push({ tone: "amber", text: "little hand wiggles" });
    if (fx.glowMinute) out.push({ tone: "blue", text: "big hand glows" });
    if (fx.ringHours) out.push({ tone: "amber", text: "number ring" });
    if (fx.ringMinutes) out.push({ tone: "blue", text: "notch ring" });
    (fx.numbers || []).forEach((n) => out.push({ tone: fx.glowMinute ? "blue" : "amber", text: `${n} lights up` }));
    if (fx.fiveCount) out.push({ tone: "blue", text: "5, 10, 15 … pop in" });
    else if (fx.fiveLabels) out.push({ tone: "blue", text: "5, 10, 15 … shown" });
    (shot.labels || []).forEach((l) => out.push({ tone: l.color === "minute" ? "blue" : "amber", text: `“${l.text}”` }));
    if (shot.readout) out.push({ tone: shot.readout.accent === "minute" ? "blue" : "amber", text: `readout ${shot.readout.sub || ""}`.trim() });
  }
  if (shot.banner) out.push({ tone: "green", text: `banner “${shot.banner}”` });
  const acts = Object.entries(shot.cast || {})
    .filter(([, v]) => v?.action && v.action !== "idle")
    .map(([k, v]) => `${CAST[k]?.name || k} ${ACTIONS[v.action]?.toLowerCase() || v.action}`);
  if (acts.length) out.push({ tone: "grey", text: acts.join(", ") });
  return out;
};

/* ------------------------------------------------------------------ */
/* Scene editor (one line, the intro, or the ending)                   */
/* ------------------------------------------------------------------ */

const Check = ({ checked, onChange, children }) => (
  <label className="sa-check lv-check">
    <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />
    {children}
  </label>
);

const QUALITIES = [
  ["standard", "Standard"],
  ["fast", "Fast (cheapest)"],
  ["best", "Best detail (costs most)"],
];

/** "✨ Make AI picture" for one lyric line. The directions and "build on the
 *  picture before" are saved on the line, so "Make pictures for every line"
 *  and every remake use them too. */
const AiPictureBox = ({ onMake, hasPicture, idea, onIdea, follow, onFollow, title, note, promptText, tint }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [quality, setQuality] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const go = async () => {
    setBusy(true);
    setError("");
    setDone(false);
    try {
      await onMake(quality);
      setDone(true);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lv-ai" style={tint ? { "--g": tint } : undefined} data-tint={tint ? "1" : undefined}>
      <div className="lv-ai-head">
        <strong>{title || "✨ Your directions for this line’s picture"}</strong>
        <span className="sa-muted">
          {note || "Leave empty to draw from the lyric. Saved with the storyboard and used every time this picture is made."}
        </span>
      </div>
      <textarea
        rows={3}
        value={idea}
        disabled={busy}
        placeholder={
          "e.g. A bar graph with 4 colourful bars (red, blue, green, yellow) on a plain white background. " +
          "The blue bar is the tallest."
        }
        onChange={(e) => onIdea(e.target.value)}
      />
      {onFollow && (
        <label className="sa-check lv-check">
          <input type="checkbox" checked={follow} disabled={busy} onChange={(e) => onFollow(e.target.checked)} />
          Build on the picture before: keep everything from the previous line’s picture and change only what I wrote
          above (e.g. “now the green bar grows taller than the blue one”)
        </label>
      )}
      <div className="lv-ed-row">
        <label className="sa-inline-field">
          Quality
          <select value={quality} disabled={busy} onChange={(e) => setQuality(e.target.value)}>
            {QUALITIES.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="lb-btn lb-btn-sm" onClick={go} disabled={busy}>
          {busy ? "Making the picture…" : hasPicture ? "✨ Make it again" : "✨ Make picture"}
        </button>
        {promptText && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setShowPrompt((v) => !v)}>
            {showPrompt ? "Hide prompt" : "👁 See the prompt"}
          </button>
        )}
        {busy && <span className="sa-muted">usually 10–40 seconds</span>}
        {done && !busy && <span className="sa-pill-inline tone-green">done — it’s on {onFollow ? "this line" : "every line of this colour"} now</span>}
      </div>
      {showPrompt && promptText && <pre className="lv-prompt">{promptText()}</pre>}
      {error && <div className="sa-banner tone-red">{error}</div>}
    </div>
  );
};

const SceneEditor = ({
  shot,
  onChange,
  kind = "line",
  previous,
  pictures = [],
  ownPicture = "",
  onMakePicture,
  linePrompt,
  group = null, // { id, g } when this line is painted a colour
  groupOptions = [], // [{ id, label }] for the "Picture colour" dropdown
  onSetGroup,
  onGroupIdea,
  onMakeGroup,
  groupPrompt,
}) => {
  const s = shot || { keep: true };
  const set = (patch) => onChange({ ...s, ...patch });
  const setFx = (patch) => {
    const fx = { ...(s.fx || {}), ...patch };
    Object.keys(fx).forEach((k) => {
      if (fx[k] === false || fx[k] === 0 || fx[k] === null || (Array.isArray(fx[k]) && !fx[k].length)) delete fx[k];
    });
    set({ fx });
  };
  const setCast = (key, action) => {
    const cast = { ...(s.cast || {}) };
    if (!action || action === "idle") delete cast[key];
    else cast[key] = { action, ...(action === "point" ? { dir: "toward" } : {}) };
    set({ cast });
  };
  const setLabel = (n, patch) => {
    const labels = [...(s.labels || [])];
    labels[n] = { color: "hour", target: "hourHand", side: n === 0 ? "left" : "right", ...(labels[n] || {}), ...patch };
    set({ labels: labels.filter((l) => l && String(l.text || "").trim()) });
  };
  const sceneType = s.keep ? "keep" : s.scene || "clock";
  const handsMode = s.clock?.spin ? "spin" : s.notchCount ? "notches" : "time";
  const fx = s.fx || {};
  const fiveMode = fx.fiveCount ? "reveal" : fx.fiveLabels ? "show" : "none";

  const changeScene = (v) => {
    if (v === "keep") return onChange({ keep: true });
    if (v === sceneType) return;
    // start from the scene above, so switching a "same scene" line on only
    // changes what you then pick
    const base = previous && !previous.keep ? clone(previous) : v === "clock" ? clone(NEW_CLOCK_SCENE) : {};
    delete base.keep;
    delete base.walkIn;
    delete base.after;
    if (v === "picture") {
      // this line's own lyric picture first, else whatever the line above showed
      const url = ownPicture || base.picture?.url || "";
      onChange({ cast: base.cast || {}, ...(base.banner ? { banner: base.banner } : {}), scene: "picture", picture: { url } });
      return;
    }
    onChange({ ...base, scene: v, ...(v === "clock" && !base.clock ? { clock: { hour: 12, minute: 0 } } : {}) });
  };
  const setPicture = (patch) => set({ picture: { ...(s.picture || {}), ...patch } });

  return (
    <div className="lv-editor">
      <div className="lv-ed-row">
        <label className="sa-inline-field">
          Scene
          <select value={sceneType} onChange={(e) => changeScene(e.target.value)}>
            {kind === "line" && <option value="keep">Same scene continues (no change)</option>}
            <option value="picture">Picture — AI-made or your own</option>
            <option value="students">Students only, with big words</option>
            <option value="clock">Clock (telling time)</option>
          </select>
        </label>
        {kind === "intro" && (
          <Check checked={s.walkIn} onChange={(v) => set({ walkIn: v })}>
            Students walk on first
          </Check>
        )}
        {kind === "outro" && (
          <label className="sa-inline-field">
            Starts
            <select value={s.after ?? 6} onChange={(e) => set({ after: Number(e.target.value) })}>
              {[3, 4, 5, 6, 8, 10, 12, 15].map((n) => (
                <option key={n} value={n}>
                  {n}s after the last line
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {sceneType === "clock" && (
        <>
          <div className="lv-ed-row">
            <label className="sa-inline-field">
              Hands
              <select
                value={handsMode}
                onChange={(e) => {
                  const v = e.target.value;
                  const hour = s.clock?.hour ?? 12;
                  const minute = s.clock?.minute ?? 0;
                  if (v === "spin") set({ clock: { spin: 4 }, notchCount: undefined });
                  else if (v === "notches") set({ clock: { hour, minute: 0 }, notchCount: true });
                  else set({ clock: { hour, minute }, notchCount: undefined });
                }}
              >
                <option value="time">Show a time</option>
                <option value="spin">Spin round (chorus)</option>
                <option value="notches">Count 1 → 5 minutes, notch by notch</option>
              </select>
            </label>
            {handsMode !== "spin" && (
              <label className="sa-inline-field">
                Time
                <select
                  value={s.clock?.hour ?? 12}
                  onChange={(e) => set({ clock: { ...(s.clock || {}), hour: Number(e.target.value) } })}
                  aria-label="Hour"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                {handsMode === "time" && (
                  <select
                    value={s.clock?.minute ?? 0}
                    onChange={(e) => set({ clock: { ...(s.clock || {}), minute: Number(e.target.value) } })}
                    aria-label="Minutes"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        :{two(m)}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            )}
            {handsMode === "spin" && (
              <label className="sa-inline-field">
                Speed
                <select
                  value={s.clock?.spin ?? 4}
                  onChange={(e) => set({ clock: { spin: Number(e.target.value) } })}
                >
                  <option value={6}>Slow</option>
                  <option value={4}>Medium</option>
                  <option value={2.5}>Fast</option>
                </select>
              </label>
            )}
          </div>

          <div className="lv-ed-row">
            <span className="lv-ed-label">Highlight</span>
            <Check checked={fx.glowHour} onChange={(v) => setFx({ glowHour: v ? 1 : 0 })}>
              Little hand glows
            </Check>
            <Check checked={fx.wobbleHour} onChange={(v) => setFx({ wobbleHour: v ? 1 : 0 })}>
              Little hand wiggles
            </Check>
            <Check checked={fx.glowMinute} onChange={(v) => setFx({ glowMinute: v ? 1 : 0 })}>
              Big hand glows
            </Check>
            <Check checked={fx.ringHours} onChange={(v) => setFx({ ringHours: v ? 1 : 0 })}>
              Number ring pulses
            </Check>
            <Check checked={fx.ringMinutes} onChange={(v) => setFx({ ringMinutes: v ? 1 : 0 })}>
              Notch ring pulses
            </Check>
          </div>

          <div className="lv-ed-row">
            <label className="sa-inline-field">
              Light up number
              <select
                value={(fx.numbers || [])[0] ?? ""}
                onChange={(e) => setFx({ numbers: e.target.value ? [Number(e.target.value)] : [] })}
              >
                <option value="">none</option>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-inline-field">
              5, 10, 15 … around the clock
              <select
                value={fiveMode}
                onChange={(e) =>
                  setFx({
                    fiveCount: e.target.value === "reveal",
                    fiveLabels: e.target.value === "show" ? 12 : 0,
                  })
                }
              >
                <option value="none">hidden</option>
                <option value="reveal">pop in one by one</option>
                <option value="show">all showing</option>
              </select>
            </label>
          </div>

          {[0, 1].map((n) => {
            const lab = (s.labels || [])[n] || {};
            if (n === 1 && !(s.labels || [])[0]) return null;
            return (
              <div className="lv-ed-row" key={n}>
                <label className="sa-inline-field lv-grow">
                  Label {n + 1}
                  <input
                    value={lab.text || ""}
                    placeholder={n === 0 ? "e.g. little hand = HOUR" : "a second label (optional)"}
                    onChange={(e) => setLabel(n, { text: e.target.value })}
                  />
                </label>
                {lab.text && (
                  <>
                    <select value={lab.color || "hour"} onChange={(e) => setLabel(n, { color: e.target.value })} aria-label="Label colour">
                      <option value="hour">orange (hours)</option>
                      <option value="minute">blue (minutes)</option>
                    </select>
                    <select value={lab.target || "hourHand"} onChange={(e) => setLabel(n, { target: e.target.value })} aria-label="Arrow points at">
                      <option value="hourHand">arrow → little hand</option>
                      <option value="minuteHand">arrow → big hand</option>
                      <option value="numbers">arrow → the numbers</option>
                      <option value="notches">arrow → the notches</option>
                    </select>
                    <select value={lab.side || (n === 0 ? "left" : "right")} onChange={(e) => setLabel(n, { side: e.target.value })} aria-label="Label side">
                      <option value="left">left side</option>
                      <option value="right">right side</option>
                    </select>
                  </>
                )}
              </div>
            );
          })}

          <div className="lv-ed-row">
            <Check
              checked={Boolean(s.readout)}
              onChange={(v) => set({ readout: v ? { sub: "", accent: "hour" } : undefined })}
            >
              Show the digital time under the clock
            </Check>
            {s.readout && (
              <>
                <label className="sa-inline-field lv-grow">
                  Words after it
                  <input
                    value={s.readout.sub || ""}
                    placeholder="e.g. 1 o'clock — or {minutes} to count"
                    onChange={(e) => set({ readout: { ...s.readout, sub: e.target.value } })}
                  />
                </label>
                <select
                  value={s.readout.accent || "hour"}
                  onChange={(e) => set({ readout: { ...s.readout, accent: e.target.value } })}
                  aria-label="Which part is coloured"
                >
                  <option value="hour">colour the hour</option>
                  <option value="minute">colour the minutes</option>
                  <option value="both">colour both</option>
                </select>
              </>
            )}
          </div>
        </>
      )}

      {kind === "line" && onSetGroup && (
        <div className="lv-ed-row">
          <label className="sa-inline-field">
            Picture colour
            <select value={group?.id || ""} onChange={(e) => onSetGroup(e.target.value)}>
              <option value="">— its own picture —</option>
              {groupOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <span className="sa-muted">Same colour = same picture as those lines (the times show when they’re sung).</span>
        </div>
      )}

      {sceneType === "picture" && (
        <>
          {group && onMakeGroup ? (
            <AiPictureBox
              key={`g-${group.id}`}
              tint={groupColor(group.g.color).hex}
              title={`● ${group.g.name || groupColor(group.g.color).name} — this picture is shared by every line of this colour`}
              note="Directions for the colour's picture. Changing them here changes them for every line of this colour."
              onMake={onMakeGroup}
              hasPicture={Boolean(group.g.url)}
              idea={group.g.idea || ""}
              onIdea={onGroupIdea}
              promptText={groupPrompt}
            />
          ) : (
            onMakePicture && (
              <AiPictureBox
                onMake={onMakePicture}
                hasPicture={Boolean(s.picture?.url)}
                idea={s.picture?.idea || ""}
                onIdea={(v) => setPicture({ idea: v })}
                follow={Boolean(s.picture?.follow)}
                onFollow={(v) => setPicture({ follow: v })}
                promptText={linePrompt}
              />
            )
          )}
          {pictures.length > 0 ? (
            <div className="lv-ed-row lv-pics">
              <span className="lv-ed-label">This song’s pictures</span>
              <div className="lv-pic-strip">
                {pictures.map((p) => (
                  <button
                    type="button"
                    key={p.i}
                    className={`lv-pic ${s.picture?.url === p.url ? "is-on" : ""} ${p.url === ownPicture ? "is-own" : ""}`}
                    title={p.text}
                    onClick={() => setPicture({ url: p.url })}
                  >
                    <img src={p.url} alt="" loading="lazy" />
                    {p.url === ownPicture && <span>this line</span>}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="sa-muted lv-ed-note">
              No pictures for this song yet. Make one above, or paste a picture link below (Image studio → Copy link).
            </p>
          )}
          <div className="lv-ed-row">
            <label className="sa-inline-field lv-grow">
              Picture link
              <input
                value={s.picture?.url || ""}
                placeholder="https://firebasestorage.googleapis.com/…"
                onChange={(e) => setPicture({ url: e.target.value.trim() })}
              />
            </label>
            <label className="sa-inline-field">
              Show it
              <select value={s.picture?.fit || "board"} onChange={(e) => setPicture({ fit: e.target.value })}>
                <option value="board">on the classroom screen</option>
                <option value="full">full screen, behind the students</option>
              </select>
            </label>
          </div>
          <div className="lv-ed-row">
            <label className="sa-inline-field">
              🎥 Camera
              <select value={s.picture?.camera || ""} onChange={(e) => setPicture({ camera: e.target.value || undefined })}>
                {CAMERAS.map((c) => (
                  <option key={c.key || "default"} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <span className="sa-muted">
              The video moves over the picture — e.g. the same bar graph zooms to the labels, then to the scale. The same
              picture on the next line glides instead of fading.
            </span>
          </div>
          <div className="lv-ed-row">
            <label className="sa-inline-field lv-grow">
              Words over the picture
              <input
                value={s.caption || ""}
                placeholder="optional — exact numbers go here, e.g. 3 × 4 = 12"
                onChange={(e) => set({ caption: e.target.value })}
              />
            </label>
          </div>
        </>
      )}

      {sceneType === "students" && (
        <div className="lv-ed-row">
          <label className="sa-inline-field lv-grow">
            Big words on screen
            <input
              value={s.caption || ""}
              placeholder="optional, e.g. We can tell time!"
              onChange={(e) => set({ caption: e.target.value })}
            />
          </label>
        </div>
      )}

      {sceneType !== "keep" && (
        <>
          <div className="lv-ed-row">
            <label className="sa-inline-field lv-grow">
              Banner at the top
              <input value={s.banner || ""} placeholder="optional" onChange={(e) => set({ banner: e.target.value })} />
            </label>
          </div>
          <div className="lv-ed-row">
            <span className="lv-ed-label">Students</span>
            {CAST_ORDER.map((key) => (
              <label key={key} className="sa-inline-field">
                {CAST[key]?.name || key}
                <select value={s.cast?.[key]?.action || "idle"} onChange={(e) => setCast(key, e.target.value)}>
                  {Object.entries(ACTIONS)
                    .filter(([a]) => a !== "walk")
                    .map(([a, label]) => (
                      <option key={a} value={a}>
                        {a === "point" ? (sceneType === "picture" ? "Point at the picture" : "Point at the clock") : label}
                      </option>
                    ))}
                </select>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Storyboard editor                                                   */
/* ------------------------------------------------------------------ */

const StoryboardEditor = ({
  sb,
  setSb,
  lines,
  cues,
  time,
  onJump,
  dirty,
  saving,
  onSave,
  onRevert,
  seedNote,
  pictureMap = {},
  slug,
  songTitle,
  onPictureMade,
}) => {
  const sung = useMemo(() => sungLinesOf(lines), [lines]);
  // this song's lyric pictures, in song order, for the scene editor's strip
  const pictures = useMemo(
    () => sung.filter((ln) => pictureMap[ln.i]).map((ln) => ({ i: ln.i, url: pictureMap[ln.i], text: ln.text })),
    [sung, pictureMap]
  );
  const usePictures = () => {
    if (
      !window.confirm(
        `Put the ${pictures.length} lines that have a lyric picture on their picture? Their clock or big-words scenes are replaced; lines without a picture don’t change. (Nothing is saved until you press Save storyboard.)`
      )
    )
      return;
    setSb(withLyricPictures(sb, lines, pictureMap).sb);
  };

  /* ---------- making AI pictures from here ---------- */

  // newest values for the async picture makers (they outlive a render)
  const sbRef = useRef(sb);
  sbRef.current = sb;
  const madeRef = useRef({}); // line -> url made this session, before React re-renders
  const [busyGroup, setBusyGroup] = useState(null);
  const [brush, setBrush] = useState(null); // colour being painted, "" = rubber

  const shotOf = (i) => sbRef.current.byLine?.[i] ?? sbRef.current.byLine?.[String(i)];
  /** The picture on line i. onlyFresh = only pictures made in this run. */
  const urlOnLine = (i, onlyFresh = false) => {
    if (madeRef.current[i]) return madeRef.current[i];
    if (onlyFresh) return "";
    const sh = shotOf(i);
    return sh && sh.scene === "picture" ? sh.picture?.url || "" : "";
  };
  const ideaOf = (i) => String(shotOf(i)?.picture?.idea || "");
  /** The first picture in song order, skipping some lines. */
  const firstUrl = (skip, onlyFresh) => {
    for (const x of sung) {
      if (!skip.has(x.i)) {
        const u = urlOnLine(x.i, onlyFresh);
        if (u) return u;
      }
    }
    return "";
  };

  /**
   * Everything sent for one picture. target = the line it's made for;
   * members = every line that will show it (a colour group, or just target).
   *  - "Same drawing in every picture": the song's first picture is attached
   *    and redrawn, changing only these directions.
   *  - "Build on the picture before" (a line's own tick): the nearest
   *    earlier picture is attached and redrawn.
   *  - otherwise an existing picture is attached for its look only.
   */
  const planFor = ({ target, members, idea, ownFollow = false, quality = "standard", ref, onlyFresh = false }) => {
    const cur = sbRef.current;
    const at = sung.findIndex((x) => x.i === target.i);
    const skip = new Set(members.map((m) => m.i));
    let prevUrl = "";
    for (let k = at - 1; k >= 0 && !prevUrl; k -= 1) if (!skip.has(sung[k].i)) prevUrl = urlOnLine(sung[k].i, onlyFresh);
    const base = cur.sameDrawing ? firstUrl(skip, onlyFresh) : "";
    const followUrl = base || (ownFollow ? prevUrl : "");
    const styleRef =
      ref !== undefined
        ? ref
        : firstUrl(skip, onlyFresh) || (onlyFresh ? "" : pictures.find((p) => !skip.has(p.i))?.url || "");
    return {
      slug,
      title: songTitle || cur.title || slug,
      lineIndex: target.i,
      line: target.text,
      before: sung.slice(Math.max(0, at - 2), at).map((x) => x.text),
      after: sung.slice(at + 1, at + 2).map((x) => x.text),
      idea,
      songStyle: cur.pictureStyle || "",
      look: lookDirections(cur),
      ownStyle: Boolean(cur.artStyle),
      alsoSung: members.map((m) => m.text),
      alsoLines: members,
      follow: Boolean(followUrl),
      refUrl: followUrl || styleRef,
      quality,
    };
  };

  /** The prompt text a plan sends, for "👁 See the prompt". */
  const promptOfPlan = (plan) =>
    `${linePicturePrompt({ ...plan, hasRef: Boolean(plan.refUrl) })}\n\n— Style sent: ${
      plan.ownStyle ? "the style above (Image studio style off)" : "SBL illustration"
    } · reference picture attached: ${plan.refUrl ? (plan.follow ? "yes — redraw it" : "yes — for its look") : "no"}`;

  const linePlan = (ln, opts = {}) => {
    const own = shotOf(ln.i)?.picture || {};
    return planFor({ target: ln, members: [ln], idea: String(own.idea || ""), ownFollow: Boolean(own.follow), ...opts });
  };
  const groupPlan = (gid, opts = {}) => {
    const g = sbRef.current.groups?.[gid] || {};
    const members = linesInGroup(sbRef.current, sung, gid);
    return planFor({ target: members[0], members, idea: String(g.idea || ""), ...opts });
  };

  /** Makes a picture for sung line `ln`, records it, and puts it on the line
   *  (and on every other line with the same words that has no colour and no
   *  scene of its own). `force` = put it on this line even if it has another scene. */
  const makeFor = async (ln, { quality = "standard", force = true, ref, onlyFresh = false } = {}) => {
    const own = shotOf(ln.i)?.picture || {};
    const idea = String(own.idea || "");
    const { url } = await makeLinePicture(linePlan(ln, { quality, ref, onlyFresh }));
    // Lines with the same words share the picture, unless one has its own directions or a colour.
    const twins = sung.filter(
      (x) =>
        x.i === ln.i ||
        (normText(x.text) === normText(ln.text) &&
          ideaOf(x.i) === idea &&
          !shotOf(x.i)?.picture?.follow &&
          !groupIdOf(sbRef.current, x.i))
    );
    twins.forEach((x) => {
      madeRef.current[x.i] = url;
      onPictureMade(x.i, url);
    });
    setSb((prev) => {
      const byLine = { ...(prev.byLine || {}) };
      twins.forEach((x) => {
        const cur = byLine[x.i];
        const free = !cur || cur.keep || (cur.scene === "picture" && !cur.picture?.url);
        if (x.i !== ln.i && !free) return;
        if (x.i === ln.i && !force && !free) return;
        const base = cur && !cur.keep && cur.scene === "picture" ? cur : { cast: cur?.cast || {} };
        byLine[x.i] = { ...base, scene: "picture", picture: { ...(base.picture || {}), url } };
      });
      return { ...prev, byLine };
    });
    return url;
  };

  /** Makes ONE picture for a colour group and puts it on every line of that colour. */
  const makeGroup = async (gid, { quality = "standard", ref, onlyFresh = false } = {}) => {
    const members = linesInGroup(sbRef.current, sung, gid);
    if (!sbRef.current.groups?.[gid] || !members.length) throw new Error("Paint some lines with this colour first.");
    setBusyGroup(gid);
    try {
      const { url } = await makeLinePicture(groupPlan(gid, { quality, ref, onlyFresh }));
      members.forEach((m) => {
        madeRef.current[m.i] = url;
        onPictureMade(m.i, url);
      });
      setSb((prev) => {
        const byLine = { ...(prev.byLine || {}) };
        const now = linesInGroup(prev, sung, gid);
        now.forEach((m) => {
          const cur = byLine[m.i] ?? byLine[String(m.i)];
          const base = cur && !cur.keep && cur.scene === "picture" ? cur : { cast: cur?.cast || {} };
          byLine[m.i] = { ...base, scene: "picture", picture: { ...(base.picture || {}), url } };
        });
        return { ...prev, byLine, groups: { ...(prev.groups || {}), [gid]: { ...(prev.groups?.[gid] || {}), url } } };
      });
      return url;
    } finally {
      setBusyGroup(null);
    }
  };

  const paintLine = (i, gid) => setSb((prev) => assignLineToGroup(prev, i, gid));

  // every time each line is sung
  const timesOf = useCallback((i) => cues.filter((c) => c.i === i).map((c) => c.t), [cues]);
  const groupOptions = useMemo(
    () =>
      Object.entries(sb.groups || {}).map(([id, g]) => {
        const ts = linesInGroup(sb, sung, id)
          .flatMap((ln) => cues.filter((c) => c.i === ln.i).map((c) => c.t))
          .sort((x, y) => x - y);
        return {
          id,
          label: `● ${g.name || groupColor(g.color).name}${ts.length ? ` — ${ts.slice(0, 5).map((t) => formatTime(t, 0)).join(", ")}${ts.length > 5 ? " …" : ""}` : ""}`,
        };
      }),
    [sb, sung, cues]
  );

  // lines with no colour whose words have no picture yet (repeated words count once)
  const missing = useMemo(() => {
    const seen = new Set();
    const shot = (i) => sb.byLine?.[i] ?? sb.byLine?.[String(i)];
    return sung.filter((ln) => {
      if (groupIdOf(sb, ln.i)) return false; // its colour makes it
      const sh = shot(ln.i);
      // lines you gave another scene (clock, big words) are left alone
      if (sh && !sh.keep && sh.scene !== "picture") return false;
      if (sh?.scene === "picture" && sh.picture?.url) return false;
      const own = sh?.picture || {};
      if (own.idea || own.follow) return true; // its own directions: its own picture
      const k = normText(ln.text);
      if (seen.has(k)) return false;
      seen.add(k);
      // the same words already have a picture on another line: reused, not remade
      return !sung.some((x) => normText(x.text) === k && pictureMap[x.i] && !groupIdOf(sb, x.i));
    });
  }, [sung, sb, pictureMap]);

  /** The work for "Make pictures": colours and lines, in song order. */
  const jobsFor = (remake) => {
    const out = [];
    const seenG = new Set();
    const seenText = new Set();
    const missingSet = new Set(missing.map((m) => m.i));
    sung.forEach((ln) => {
      const gid = groupIdOf(sb, ln.i);
      if (gid && sb.groups?.[gid]) {
        if (!seenG.has(gid) && (remake || !sb.groups[gid].url)) out.push({ gid, text: sb.groups[gid].name || "colour" });
        seenG.add(gid);
        return;
      }
      if (gid) return;
      if (missingSet.has(ln.i)) {
        out.push({ ln, text: ln.text });
        return;
      }
      if (!remake) return;
      const sh = sb.byLine?.[ln.i] ?? sb.byLine?.[String(ln.i)];
      if (!(sh && sh.scene === "picture" && sh.picture?.url)) return;
      const own = sh.picture || {};
      const k = normText(ln.text);
      if (!own.idea && !own.follow) {
        if (seenText.has(k)) return;
        seenText.add(k);
      }
      out.push({ ln, text: ln.text });
    });
    return out;
  };
  const toMake = jobsFor(false);
  const toRemake = jobsFor(true);

  const [batch, setBatch] = useState(null); // { done, total, failed, current }
  const stopRef = useRef(false);

  const makeAll = async (remake = false) => {
    const jobs = remake ? toRemake : toMake;
    if (!jobs.length) return;
    const n = jobs.length;
    const st = sb.artStyle ? `“${sb.artStyle}” style` : "SBL style";
    if (
      !window.confirm(
        (remake
          ? `Make all ${n} picture${n === 1 ? "" : "s"} again, from scratch, in the ${st}? The old ones stay in storage.\n\n`
          : `Make ${n} AI picture${n === 1 ? "" : "s"} — one per colour and one for each other line without a picture?\n\n`) +
          `Roughly $${(n * 0.04).toFixed(2)}–$${(n * 0.13).toFixed(2)} on the Gemini bill, about ${Math.max(1, Math.round((n * 25) / 60))} minute${n * 25 >= 90 ? "s" : ""}. ` +
          "You can stop at any time; pictures already made are kept.\n\n" +
          "Each uses the picture look, “What every picture shows” and its own directions. Lines with a clock or big-words scene keep it."
      )
    )
      return;
    stopRef.current = false;
    if (remake) madeRef.current = {};
    let done = 0;
    let failed = 0;
    const errors = [];
    for (const job of jobs) {
      if (stopRef.current) break;
      setBatch({ done, total: n, failed, current: job.text });
      try {
        // eslint-disable-next-line no-await-in-loop
        if (job.gid) await makeGroup(job.gid, { onlyFresh: remake });
        // eslint-disable-next-line no-await-in-loop
        else await makeFor(job.ln, { force: remake, onlyFresh: remake });
        done += 1;
      } catch (err) {
        failed += 1;
        errors.push(err?.message || String(err));
        if (/deployed|admin/i.test(err?.message || "")) {
          setBatch({ done, total: n, failed, current: "", error: err.message });
          return;
        }
      }
    }
    setBatch({ done, total: n, failed, current: "", finished: true, stopped: stopRef.current, lastError: errors.at(-1) });
  };
  const batchRunning = Boolean(batch && !batch.finished && !batch.error);
  const [openRow, setOpenRow] = useState(null);
  const [tplKey, setTplKey] = useState(sb.template || STORYBOARDS[0]?.key || "");

  // first time each line is sung, and how many times
  const firstCue = useMemo(() => {
    const out = {};
    cues.forEach((c) => {
      if (!out[c.i]) out[c.i] = { t: c.t, n: 0 };
      out[c.i].n += 1;
    });
    return out;
  }, [cues]);

  const nowLine = useMemo(() => {
    let found = null;
    for (const c of cues) {
      if (c.t <= time) found = c.i;
      else break;
    }
    return found;
  }, [cues, time]);

  const setLine = (i, shot) => setSb({ ...sb, byLine: { ...sb.byLine, [i]: shot } });
  const prevShot = (idx) => {
    for (let k = idx - 1; k >= 0; k -= 1) {
      const sh = sb.byLine[sung[k].i];
      if (sh && !sh.keep) return sh;
    }
    return sb.intro;
  };

  const applyTemplate = () => {
    const tpl = templateByKey(tplKey);
    if (!tpl) return;
    if (!window.confirm(`Replace every scene with the “${tpl.title}” template? (Nothing is saved until you press Save storyboard.)`)) return;
    setSb(seedFromTemplate(tpl, lines, sb.title));
  };

  return (
    <div className="lv-board">
      <div className="lv-board-head">
        <div>
          <strong>Storyboard</strong>
          <span className="sa-muted">
            What happens on each line of the song. Click a line to jump the preview there; press Edit to change
            its scene. <em>When</em> each line starts comes from the Sing-along studio.
          </span>
        </div>
        <div className="lv-board-tools">
          <label className="sa-inline-field">
            Video title
            <input value={sb.title || ""} onChange={(e) => setSb({ ...sb, title: e.target.value })} />
          </label>
          {STORYBOARDS.length > 0 && (
            <span className="sa-inline-field">
              <select value={tplKey} onChange={(e) => setTplKey(e.target.value)} aria-label="Template">
                {STORYBOARDS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.storyboard.title} template
                  </option>
                ))}
              </select>
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={applyTemplate}>
                Fill from template
              </button>
            </span>
          )}
          {pictures.length > 0 && (
            <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={usePictures}>
              🖼 Use lyric pictures ({pictures.length})
            </button>
          )}
        </div>
      </div>

      <PictureLook sb={sb} setSb={setSb} />

      <StorylineBox sb={sb} setSb={setSb} sung={sung} />

      <GroupsBar
        sb={sb}
        setSb={setSb}
        sung={sung}
        timesOf={timesOf}
        brush={brush}
        setBrush={setBrush}
        onMake={(gid, quality) => makeGroup(gid, { quality })}
        busyGroup={batchRunning ? busyGroup || "*" : busyGroup}
        promptOf={(gid) => promptOfPlan(groupPlan(gid))}
      />

      <div className="lv-make-bar">
        {toMake.length > 0 && !batchRunning && (
          <button type="button" className="lb-btn" onClick={() => makeAll(false)}>
            ✨ Make pictures ({toMake.length})
          </button>
        )}
        {toMake.length === 0 && toRemake.length > 0 && !batchRunning && (
          <span className="sa-pill-inline tone-green">every line has its picture</span>
        )}
        {toRemake.length > 0 && !batchRunning && (
          <button
            type="button"
            className="lb-btn lb-btn-ghost lb-btn-sm"
            onClick={() => makeAll(true)}
            title="After changing the style or background: make every picture again so they all match"
          >
            ↻ Make every picture again ({toRemake.length})
          </button>
        )}
        <span className="sa-muted">
          One picture per colour, plus one for each other line without a picture. Changed the style? Use ↻ so every
          picture matches.
        </span>
      </div>

      {batch && (
        <div className={`sa-banner tone-${batch.error ? "red" : batch.finished ? (batch.failed ? "amber" : "green") : "blue"}`}>
          {batch.error ? (
            <>Stopped: {batch.error}</>
          ) : batch.finished ? (
            <>
              {batch.stopped ? "Stopped. " : ""}Made {batch.done} picture{batch.done === 1 ? "" : "s"}
              {batch.failed ? `; ${batch.failed} didn’t work — press the button again to retry those` : ""}. Check the
              counting in each one, add exact numbers in “Words over the picture”, then Save storyboard.
              {batch.lastError ? <> (Last problem: {batch.lastError})</> : null}
            </>
          ) : (
            <>
              Making picture {batch.done + batch.failed + 1} of {batch.total}: “{batch.current}”… keep this tab open.{" "}
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => (stopRef.current = true)}>
                Stop after this one
              </button>
            </>
          )}
          {(batch.finished || batch.error) && (
            <button type="button" className="sa-x" onClick={() => setBatch(null)} aria-label="Dismiss">
              ✕
            </button>
          )}
        </div>
      )}

      {seedNote && <div className={`sa-banner tone-${seedNote.tone}`}>{seedNote.text}</div>}

      <div className="lv-rows">
        {/* intro */}
        <div className={`lv-row ${openRow === "intro" ? "is-open" : ""}`}>
          <div className="lv-row-main" onClick={() => onJump(0)} role="button" tabIndex={-1}>
            <span className="lv-row-time">0:00</span>
            <span className="lv-row-text">
              <span className="lv-row-section">Intro</span> Music before the first line
              {sb.title ? ` · title “${sb.title}”` : ""}
            </span>
            <span className="lv-row-tags">
              {sb.intro?.walkIn && <span className="sa-pill-inline tone-grey">students walk on</span>}
              {describe({ ...(sb.intro || {}), walkIn: undefined }).map((d, k) => (
                <span key={k} className={`sa-pill-inline tone-${d.tone}`}>
                  {d.text}
                </span>
              ))}
            </span>
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                setOpenRow(openRow === "intro" ? null : "intro");
              }}
            >
              {openRow === "intro" ? "Done" : "Edit"}
            </button>
          </div>
          {openRow === "intro" && (
            <SceneEditor kind="intro" shot={sb.intro} pictures={pictures} onChange={(shot) => setSb({ ...sb, intro: shot })} />
          )}
        </div>

        {/* every sung line */}
        {sung.map((ln, idx) => {
          const shot = sb.byLine[ln.i] || { keep: true };
          const fc = firstCue[ln.i];
          const isNow = nowLine === ln.i;
          const isOpen = openRow === ln.i;
          const gid = groupIdOf(sb, ln.i);
          const grp = gid ? sb.groups?.[gid] : null;
          const gHex = grp ? groupColor(grp.color).hex : "";
          return (
            <div
              key={ln.i}
              className={`lv-row ${isNow ? "is-now" : ""} ${isOpen ? "is-open" : ""} ${shot.keep ? "is-keep" : ""} ${grp ? "has-group" : ""} ${brush !== null ? "is-painting" : ""}`}
              style={grp ? { "--g": gHex } : undefined}
            >
              <div
                className="lv-row-main"
                onClick={() => {
                  // painting: clicking the line paints it
                  if (brush !== null) paintLine(ln.i, brush === "" || gid === brush ? "" : brush);
                  else if (fc) onJump(fc.t);
                }}
                role="button"
                tabIndex={-1}
              >
                <span className="lv-row-time">
                  <LineDot sb={sb} i={ln.i} brush={brush} onPaint={paintLine} />
                  {fc ? formatTime(fc.t) : "—"}
                </span>
                <span className="lv-row-text">
                  {ln.section && <span className="lv-row-section">{ln.section}</span>} {ln.text}
                  {fc && fc.n > 1 ? <span className="sa-muted"> · sung {fc.n}×</span> : null}
                  {!fc && <span className="sa-pill-inline tone-amber">not timed</span>}
                </span>
                <span className="lv-row-tags">
                  {grp && (
                    <span className="sa-pill-inline lv-group-pill" style={{ "--g": gHex }}>
                      ● {grp.name || groupColor(grp.color).name}
                    </span>
                  )}
                  {shot.scene === "picture" && shot.picture?.url && (
                    <img className="lv-row-thumb" src={shot.picture.url} alt="" loading="lazy" />
                  )}
                  {describe(shot).map((d, k) => (
                    <span key={k} className={`sa-pill-inline tone-${d.tone}`}>
                      {d.text}
                    </span>
                  ))}
                  {shot.keep && pictureMap[ln.i] && <span className="sa-pill-inline tone-blue">picture ready</span>}
                </span>
                <button
                  type="button"
                  className="lb-btn lb-btn-ghost lb-btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenRow(isOpen ? null : ln.i);
                    if (!isOpen && fc) onJump(fc.t);
                  }}
                >
                  {isOpen ? "Done" : "Edit"}
                </button>
              </div>
              {isOpen && (
                <>
                  <SceneEditor
                    shot={shot}
                    previous={prevShot(idx)}
                    pictures={pictures}
                    ownPicture={pictureMap[ln.i] || ""}
                    onChange={(next) => setLine(ln.i, next)}
                    onMakePicture={(quality) => makeFor(ln, { quality, force: true })}
                    linePrompt={() => promptOfPlan(linePlan(ln))}
                    group={grp ? { id: gid, g: grp } : null}
                    groupOptions={groupOptions}
                    onSetGroup={(g) => paintLine(ln.i, g)}
                    onGroupIdea={(v) =>
                      setSb((prev) => ({
                        ...prev,
                        groups: { ...prev.groups, [gid]: { ...prev.groups[gid], idea: v } },
                      }))
                    }
                    onMakeGroup={(quality) => makeGroup(gid, { quality })}
                    groupPrompt={() => promptOfPlan(groupPlan(gid))}
                  />
                  <div className="lv-ed-row lv-ed-foot">
                    <button
                      type="button"
                      className="lb-btn lb-btn-ghost lb-btn-sm"
                      onClick={() => {
                        const p = clone(prevShot(idx));
                        if (p) {
                          delete p.walkIn;
                          delete p.after;
                          setLine(ln.i, { ...p, keep: undefined });
                        }
                      }}
                    >
                      Copy the scene above
                    </button>
                    {idx + 1 < sung.length && (
                      <button
                        type="button"
                        className="lb-btn lb-btn-ghost lb-btn-sm"
                        onClick={() => {
                          setOpenRow(sung[idx + 1].i);
                          const nf = firstCue[sung[idx + 1].i];
                          if (nf) onJump(nf.t);
                        }}
                      >
                        Next line ↓
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* ending */}
        <div className={`lv-row ${openRow === "outro" ? "is-open" : ""}`}>
          <div className="lv-row-main" role="button" tabIndex={-1}>
            <span className="lv-row-time">end</span>
            <span className="lv-row-text">
              <span className="lv-row-section">Ending</span> After the last line
            </span>
            <span className="lv-row-tags">
              {describe({ ...(sb.outro || {}), after: undefined }).map((d, k) => (
                <span key={k} className={`sa-pill-inline tone-${d.tone}`}>
                  {d.text}
                </span>
              ))}
            </span>
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              onClick={() => setOpenRow(openRow === "outro" ? null : "outro")}
            >
              {openRow === "outro" ? "Done" : "Edit"}
            </button>
          </div>
          {openRow === "outro" && (
            <SceneEditor kind="outro" shot={sb.outro} pictures={pictures} onChange={(shot) => setSb({ ...sb, outro: shot })} />
          )}
        </div>
      </div>

      <div className="sa-savebar">
        <span className="sa-muted sa-savebar-note">
          {dirty ? "Unsaved changes — the preview already shows them." : "All changes saved."}
        </span>
        {dirty && (
          <button type="button" className="lb-btn lb-btn-ghost" onClick={onRevert} disabled={saving}>
            Undo changes
          </button>
        )}
        <button type="button" className="lb-btn lb-btn-save" onClick={onSave} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save storyboard"}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Studio: one topic                                                   */
/* ------------------------------------------------------------------ */

const Studio = ({ topic, sync, saved, pictureMap = {}, onClose, onSaved, onStoryboardSaved, onVideoAttached }) => {
  const slug = topic.slug;
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const clockRef = useRef(null);
  const uiAtRef = useRef(0);
  const logo = useLogo();

  const lines = useMemo(() => toLyricLines(sync.lines), [sync]);
  const cues = useMemo(() => cleanCues(sync.cues, parseLyrics(lines)), [sync, lines]);
  const songUrl = fullSongUrlOf(sync, topic.musicUrl);
  const endAt = endAtOf(sync);
  const stopAt = stopTimeOf(endAt);

  // the storyboard being edited: saved one, else a template draft, else blank
  const [initial] = useState(() => {
    if (saved?.byLine) return { sb: clone(saved), note: null };
    const tpl = templateForTopic(topic);
    if (tpl) {
      const sb = seedFromTemplate(tpl.storyboard, lines, topic.title || tpl.storyboard.title);
      const miss = sb.total - sb.matched;
      return {
        sb,
        note: {
          tone: miss ? "amber" : "blue",
          text: miss
            ? `Started from the ${tpl.storyboard.title} template: ${sb.matched} of ${sb.total} lines matched. Lines showing “same scene continues” didn’t match the template’s wording — press Edit on them and choose their scene, then Save storyboard.`
            : `Started from the ${tpl.storyboard.title} template: all ${sb.total} lines matched. Check it, then Save storyboard.`,
        },
      };
    }
    const picked = withLyricPictures(blankStoryboard(lines, topic.title), lines, pictureMap);
    if (picked.used) {
      const total = sungLinesOf(lines).length;
      return {
        sb: picked.sb,
        note: {
          tone: picked.used < total ? "amber" : "blue",
          text:
            `Started from this song’s lyric pictures: ${picked.used} of ${total} lines are on their picture.` +
            (picked.used < total ? " The rest show “same scene continues” — Edit them if you want." : "") +
            " Put any exact numbers in “Words over the picture”, then Save storyboard.",
        },
      };
    }
    return {
      sb: blankStoryboard(lines, topic.title),
      note: {
        tone: "blue",
        text: "No storyboard yet. Every line starts as “same scene”; press Edit on a line to give it a scene, then Save storyboard.",
      },
    };
  });
  const [sb, setSbState] = useState(initial.sb);
  const [savedSb, setSavedSb] = useState(saved?.byLine ? clone(saved) : null);
  const [dirty, setDirty] = useState(!saved?.byLine);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  // accepts a value or an updater (pictures finish while you keep editing)
  const setSb = (next) => {
    setSbState((prev) => (typeof next === "function" ? next(prev) : next));
    setDirty(true);
  };
  // this song's pictures, including ones made in this session
  const [pics, setPics] = useState(pictureMap);
  const onPictureMade = useCallback((i, url) => setPics((p) => ({ ...p, [i]: url })), []);

  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [audioError, setAudioError] = useState("");

  const timeline = useMemo(
    () => buildTimeline({ storyboard: sb, lines, cues, duration: duration || undefined }),
    [sb, lines, cues, duration]
  );

  /* ---------- drawing ---------- */

  const draw = useCallback(
    (t) => {
      const c = canvasRef.current;
      if (!c) return;
      drawScaled(c.getContext("2d"), c.width, c.height, {
        t,
        timeline,
        lines,
        title: sb.title,
        logo,
        fade: endAt !== null && t > endAt ? fadeLevelAt(endAt, t) : 1,
      });
    },
    [timeline, lines, sb.title, logo, endAt]
  );

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!clockRef.current) clockRef.current = makeSmoothClock(a);
    const now = clockRef.current.now();
    if (stopAt !== null && endAt !== null) {
      if (a.currentTime >= stopAt) {
        a.pause();
        a.volume = 1;
      } else {
        a.volume = fileFadesItself(a.duration, stopAt) ? 1 : fadeLevelAt(endAt, a.currentTime);
      }
    }
    if (Math.abs(now - uiAtRef.current) > 0.2 || a.paused) {
      uiAtRef.current = now;
      setTime(now);
    }
    draw(now);
    // always continue with the newest tick, so storyboard edits show live
    if (!a.paused && !a.ended) rafRef.current = requestAnimationFrame(() => tickRef.current());
  }, [draw, endAt, stopAt]);

  // keep the newest tick for the running animation loop
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    draw(audioRef.current?.currentTime || 0);
  }, [draw]);

  // Pictures load in the background; redraw a paused preview as each arrives.
  useEffect(() => {
    preloadPictures(pictureUrlsOf(sb));
  }, [sb]);
  useEffect(
    () =>
      onPictureLoaded(() => {
        const a = audioRef.current;
        if (!a || a.paused) draw(a?.currentTime || 0);
      }),
    [draw]
  );

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    if (stopAt !== null && a.currentTime >= stopAt - 0.05) a.currentTime = 0;
    try {
      await a.play();
    } catch {
      setAudioError("The song would not play. Check the song file in the Topic Library.");
    }
  };

  const seek = (t) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(Number.isFinite(a.duration) ? a.duration : t, t));
    if (a.volume !== 1 && (endAt === null || a.currentTime < endAt)) a.volume = 1;
    setTime(a.currentTime);
    draw(a.currentTime);
  };

  // jump a little before the line so you see the scene change
  const jump = (t) => seek(Math.max(0, t - 0.8));

  const save = async () => {
    setSaving(true);
    try {
      const out = await saveLessonStoryboard(slug, sb);
      setSavedSb(clone(out));
      setDirty(false);
      onStoryboardSaved({ ...out, id: slug });
      setNotice({ tone: "ok", text: "Storyboard saved." });
    } catch (err) {
      setNotice({
        tone: "error",
        text:
          err?.code === "permission-denied"
            ? "Permission denied saving the storyboard. Deploy the firestore.rules with the lessonStoryboards block."
            : `Could not save: ${err?.message || err}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (savedSb) {
      setSbState(clone(savedSb));
      setDirty(false);
    } else {
      setSbState(initial.sb);
    }
  };

  return (
    <div className="sa-studio lv-studio">
      <div className="sa-studio-head">
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            if (dirty && !window.confirm("The storyboard has unsaved changes. Leave without saving?")) return;
            onClose();
          }}
        >
          ← All songs
        </button>
        <div className="sa-studio-title">
          <h3>{sb.title || topic.title || slug}</h3>
          <span className="sa-muted">
            {slug} · {fileNameOf(songUrl)}
            {endAt !== null ? ` · ends at ${formatTime(endAt)} with a fade` : ""}
          </span>
        </div>
        {dirty && <span className="sa-pill-inline tone-amber">Unsaved storyboard</span>}
      </div>

      {sync.reviewed === false && (
        <div className="sa-banner tone-amber">
          <strong>These timings are straight from auto-sync and haven’t been checked yet.</strong> If lines change
          too early or late, fix them in the Sing-along studio (− / + on each line, then Save) — the video follows.
        </div>
      )}
      {audioError && <div className="sa-banner tone-red">{audioError}</div>}
      {notice && (
        <div className={`sa-notice tone-${notice.tone}`}>
          {notice.text}
          <button type="button" className="sa-x" onClick={() => setNotice(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      <audio
        ref={audioRef}
        src={songUrl}
        preload="auto"
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onPlay={() => {
          setPlaying(true);
          cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(() => tickRef.current());
        }}
        onPause={() => setPlaying(false)}
        onSeeked={() => draw(audioRef.current?.currentTime || 0)}
        onError={() => setAudioError("This song file could not be loaded. Check the Song field in the Topic Library.")}
      />

      <div className="lv-work">
      <div className="lv-sticky">
        <canvas ref={canvasRef} className="lv-stage" width={BASE_W} height={BASE_H} aria-label="Lesson video preview" />
        <div className="sa-transport">
          <button type="button" className="lb-btn sa-play" onClick={togglePlay}>
            {playing ? "❚❚ Pause" : "▶ Play"}
          </button>
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time - 5)}>
            −5s
          </button>
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time + 5)}>
            +5s
          </button>
          <span className="sa-clock">
            {formatTime(time)} / {formatTime(stopAt !== null ? Math.min(stopAt, duration || stopAt) : duration, 0)}
          </span>
          <input
            type="range"
            className="sa-seek lv-seek"
            min={0}
            max={duration || 0}
            step={0.01}
            value={Math.min(time, duration || 0)}
            onChange={(e) => seek(Number(e.target.value))}
            aria-label="Video position"
          />
        </div>
      </div>

      <StoryboardEditor
        sb={sb}
        setSb={setSb}
        lines={lines}
        cues={cues}
        time={time}
        onJump={jump}
        dirty={dirty}
        saving={saving}
        onSave={save}
        onRevert={revert}
        seedNote={!savedSb ? initial.note : null}
        pictureMap={pics}
        slug={slug}
        songTitle={topic.title || slug}
        onPictureMade={onPictureMade}
      />
      </div>

      <ExportBox
        topic={topic}
        slug={slug}
        sync={sync}
        sb={sb}
        dirty={dirty}
        songUrl={songUrl}
        endAt={endAt}
        timeline={timeline}
        lines={lines}
        onSaved={onSaved}
        onVideoAttached={onVideoAttached}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* MP4 export                                                          */
/* ------------------------------------------------------------------ */

const ExportBox = ({ topic, slug, sync, sb, dirty, songUrl, endAt, timeline, lines, onSaved, onVideoAttached }) => {
  const [size, setSize] = useState("720");
  const [audible, setAudible] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | done | uploading | saved | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // "Checking this browser can play MP4…" — shown under the progress bar.
  const [status, setStatus] = useState("");
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      if (result?.url) URL.revokeObjectURL(result.url);
      abortRef.current?.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const supported = canExportVideo();
  const mime = pickVideoMime();
  const willBeMp4 = Boolean(mime && mime.startsWith("video/mp4"));
  const sig = videoSignature(sync, sb);
  const saved = sync?.lessonVideoUrl;
  const savedCurrent = saved && sync.lessonVideoSig === sig;
  const onTopic = topic.videoUrl && saved && mediaKey(topic.videoUrl) === mediaKey(saved);

  const make = async () => {
    setError(null);
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
    setProgress(0);
    setPhase("recording");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Every picture must be loaded (with CORS) before recording, or its
      // scenes record blank.
      const urls = pictureUrlsOf(timeline);
      if (urls.length) {
        setStatus(`Loading ${urls.length} picture${urls.length === 1 ? "" : "s"}…`);
        const pre = await preloadPictures(urls);
        if (!pre.ok) {
          throw Object.assign(
            new Error(
              `${pre.failed.length} picture${pre.failed.length === 1 ? "" : "s"} couldn’t be loaded for recording, so ${
                pre.failed.length === 1 ? "its scene" : "their scenes"
              } would come out blank. Check the picture link${pre.failed.length === 1 ? "" : "s"} in the storyboard; for files in SBL storage this means the bucket’s CORS doesn’t list ${window.location.origin}.`
            ),
            { code: "pictures" }
          );
        }
        setStatus("");
      }
      const out = await exportSingAlongVideo({
        musicUrl: songUrl,
        width: SIZES[size].width,
        height: SIZES[size].height,
        audible,
        endAt,
        logoUrl: SBL_LOGOS.white,
        renderFrame: (ctx, { width, height, t, fade, logo }) =>
          drawScaled(ctx, width, height, { t, timeline, lines, title: sb.title, logo, fade }),
        signal: controller.signal,
        onProgress: (f) => setProgress(f),
        onStatus: setStatus,
      });
      setResult({ ...out, url: URL.createObjectURL(out.blob), sig });
      setPhase("done");
    } catch (err) {
      if (err?.code === "aborted") setPhase("idle");
      else {
        setError({ code: err?.code || "error", message: err?.message || String(err) });
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
      setStatus("");
    }
  };

  const upload = async () => {
    if (!result) return;
    setPhase("uploading");
    setProgress(0);
    try {
      // videos/{topic}{year}animated.mp4 — its own word, so it can never land
      // on (and overwrite) the topic's existing video or sing-along video.
      const path = `videos/${animatedFileName(slug, result.ext)}`;
      const task = uploadBytesResumable(storageRef(storage, path), result.blob, { contentType: result.mime });
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => setProgress(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      const savedDoc = await saveLyricSync(slug, {
        lessonVideoUrl: url,
        lessonVideoPath: path,
        lessonVideoSig: result.sig,
        lessonVideoMadeAt: new Date().toISOString(),
      });
      onSaved({ ...(sync || {}), ...savedDoc, id: slug });
      if (onTopic) {
        await attachTopicVideo(topic.id || slug, url);
        onVideoAttached(url);
      }
      setPhase("saved");
    } catch (err) {
      setError({ code: "upload", message: err?.message || String(err) });
      setPhase("error");
    }
  };

  const putOnTopic = async () => {
    if (!saved) return;
    const current = topic.videoUrl ? fileNameOf(topic.videoUrl) : "";
    if (
      current &&
      !window.confirm(
        `Replace ${current} on this topic's Video button with the animated video? The old file stays in storage, and you can switch back in the Topic Library.`
      )
    )
      return;
    setBusy(true);
    try {
      await attachTopicVideo(topic.id || slug, saved);
      onVideoAttached(saved);
    } catch (err) {
      setError({ code: "attach", message: `Could not update the topic: ${err?.message || err}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sa-export">
      <div className="sa-export-head">
        <strong>🎬 Make the video</strong>
        <span className="sa-muted">
          Records exactly what the preview shows, with the song, as a{" "}
          {willBeMp4 ? "MP4" : "WebM (use current Chrome or Edge for MP4)"} file. It records in real time, so keep
          this tab open and on screen until it finishes.
          {endAt !== null ? ` It stops after the sing-along’s fade-out at ${formatTime(stopTimeOf(endAt))}.` : ""}
        </span>
      </div>

      {saved && (
        <div className="sa-export-current">
          Saved video:{" "}
          <a href={saved} target="_blank" rel="noreferrer">
            {sync.lessonVideoPath || "open"} ↗
          </a>{" "}
          <span className={`sa-pill-inline tone-${savedCurrent ? "green" : "amber"}`}>
            {savedCurrent ? "up to date" : "out of date — timings or storyboard changed since"}
          </span>
        </div>
      )}

      <div className="sa-export-current">
        This topic’s Video button:{" "}
        {onTopic ? (
          <span className="sa-pill-inline tone-green">plays the animated video</span>
        ) : (
          <>
            <span className="sa-pill-inline tone-grey">
              {topic.videoUrl ? `plays ${fileNameOf(topic.videoUrl)}` : "no video"}
            </span>
            {saved && (
              <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" disabled={busy} onClick={putOnTopic}>
                Use the animated video
              </button>
            )}
          </>
        )}
      </div>

      {!supported ? (
        <div className="sa-banner tone-amber">
          This browser can’t record video. Open the Layout Builder in the latest Chrome or Edge on a computer.
        </div>
      ) : (
        <>
          <div className="sa-export-row">
            <label className="sa-inline-field">
              Size
              <select value={size} onChange={(e) => setSize(e.target.value)} disabled={phase === "recording"}>
                {Object.entries(SIZES).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-check">
              <input
                type="checkbox"
                checked={audible}
                onChange={(e) => setAudible(e.target.checked)}
                disabled={phase === "recording"}
              />
              Play the song out loud while recording
            </label>
            {phase === "recording" ? (
              <button type="button" className="lb-btn lb-btn-danger" onClick={() => abortRef.current?.abort()}>
                ■ Cancel
              </button>
            ) : (
              <button
                type="button"
                className="lb-btn"
                onClick={make}
                disabled={phase === "uploading" || dirty}
                title={dirty ? "Save the storyboard first" : ""}
              >
                🎬 Make video
              </button>
            )}
          </div>
          {dirty && <p className="sa-muted">Save the storyboard first — the video is made from the saved version.</p>}

          {(phase === "recording" || phase === "uploading") && (
            <div className="sa-progress">
              <div className="sa-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
              <span>
                {phase === "recording" ? "Recording" : "Uploading"} {Math.round(progress * 100)}%
                {phase === "recording" ? " — keep this tab visible" : ""}
              </span>
            </div>
          )}

          {status && phase === "recording" && <p className="sa-muted">{status}</p>}

          {error && (
            <div className="sa-banner tone-red">
              {error.code === "cors"
                ? "The browser wasn’t allowed to download the song for recording. Your storage bucket’s CORS setting has to list this site; see the Sing-along studio’s help for the one-time fix."
                : error.message}
            </div>
          )}

          {result && ["done", "uploading", "saved"].includes(phase) && (
            <div className="sa-export-result">
              <ExportPreview url={result.url} label={result.codecLabel} />
              {result.logoMissing && (
                <div className="sa-banner tone-amber" style={{ marginTop: 10 }}>
                  Made <strong>without the SBL logo</strong>: the browser wasn’t allowed to load the logo file. Add{" "}
                  <code>{window.location.origin}</code> to your bucket’s CORS origins and make it again.
                </div>
              )}
              <div className="sa-export-actions">
                <a className="lb-btn lb-btn-soft" href={result.url} download={`${slug}-animated.${result.ext}`}>
                  ⬇ Download {result.ext.toUpperCase()}
                </a>
                <button type="button" className="lb-btn" onClick={upload} disabled={phase === "uploading" || phase === "saved"}>
                  {phase === "saved" ? "✓ Saved to SBL" : "☁ Save to SBL storage"}
                </button>
              </div>
              {phase === "saved" && (
                <p className="sa-muted">
                  Saved as <code>videos/{animatedFileName(slug, result.ext)}</code>.{" "}
                  {onTopic
                    ? "This topic’s Video button now plays the new file."
                    : "Press “Use the animated video” above to put it on this topic’s Video button."}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel: pick a song                                                  */
/* ------------------------------------------------------------------ */

const LessonVideoPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);
  const [syncs, setSyncs] = useState({});
  const [boards, setBoards] = useState({});
  const [pics, setPics] = useState({});
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, s, b, p] = await Promise.all([
        getAllLibraryTopics(),
        getAllLyricSyncs(),
        getAllLessonStoryboards().catch((err) => {
          if (err?.code === "permission-denied") throw err;
          return {};
        }),
        getAllLyricImages().catch(() => ({})),
      ]);
      setTopics(t);
      setSyncs(s);
      setBoards(b);
      setPics(p);
    } catch (err) {
      setError(
        err?.code === "permission-denied"
          ? "Permission denied reading lyricSync or lessonStoryboards. Deploy the updated firestore.rules."
          : `Could not load: ${err?.message || err}`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toolKey = useMemo(() => makeToolKey(topics), [topics]);

  // every topic with a song, with its sing-along, storyboard and template
  const rows = useMemo(
    () =>
      topics
        .filter((t) => t.musicUrl)
        .map((topic) => {
          const slug = toolKey(topic);
          const sync =
            syncs[slug] ||
            (slug === legacyKey(topic.slug || topic.id) ? syncs[exactKey(topic.slug || topic.id)] : null) ||
            null;
          const t = { ...topic, id: topic.id || topic.slug, slug };
          const board = boards[slug] || null;
          const template = board ? null : templateForTopic(t);
          const pictureMap = lyricImageUrls(pics[slug]);
          const pictureCount = Object.keys(pictureMap).length;
          let status = { key: "ready", label: "Ready", tone: "blue" };
          if (!sync || !(sync.cues || []).length) status = { key: "nosync", label: "Song not synced yet", tone: "grey" };
          else if (!syncMatchesSong(sync, topic.musicUrl)) status = { key: "stale", label: "Song changed — re-sync", tone: "red" };
          else if (sync.reviewed === false) status = { key: "auto", label: "Timings not checked", tone: "amber" };
          else if (sync.lessonVideoUrl) status = { key: "made", label: "Video made", tone: "green" };
          return { topic: t, sync, board, template, status, pictureMap, pictureCount };
        })
        .sort((a, b) => {
          const rank = (r) => (r.board ? 0 : r.template || r.pictureCount ? 1 : 2);
          return rank(a) - rank(b) || String(a.topic.title || a.topic.slug).localeCompare(String(b.topic.title || b.topic.slug));
        }),
    [topics, syncs, boards, pics, toolKey]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) return String(r.topic.title || "").toLowerCase().includes(q) || r.topic.slug.includes(q);
      return showAll || r.board || r.template || r.pictureCount;
    });
  }, [rows, search, showAll]);

  const active = activeSlug ? rows.find((r) => r.topic.slug === activeSlug) : null;
  const canOpen = (r) => ["ready", "auto", "made"].includes(r.status.key);

  const openStudio = (slug) => {
    setActiveSlug(slug);
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  return (
    <section className="lb-card sa-panel lv-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step sa-step">🎬</span>
        <div>
          <h2>Lesson video studio</h2>
          <p className="lb-hint">
            Animated videos with the SBL students, a teaching scene and the lyrics along the bottom. Set what happens
            on each lyric line in the storyboard; the timing comes from the song’s sing-along.
          </p>
        </div>
        <div className="sa-head-actions">
          {open && !active && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          {error && <div className="sa-banner tone-red">{error}</div>}

          {active && canOpen(active) ? (
            <Studio
              key={active.topic.slug}
              topic={active.topic}
              sync={active.sync}
              saved={active.board}
              pictureMap={active.pictureMap}
              onClose={() => setActiveSlug(null)}
              onSaved={(saved) =>
                setSyncs((prev) => ({ ...prev, [active.topic.slug]: { ...(prev[active.topic.slug] || {}), ...saved } }))
              }
              onStoryboardSaved={(b) => setBoards((prev) => ({ ...prev, [active.topic.slug]: b }))}
              onVideoAttached={(url) =>
                setTopics((prev) =>
                  prev.map((t) => ((t.id || t.slug) === active.topic.id ? { ...t, videoUrl: url } : t))
                )
              }
            />
          ) : (
            <>
              <div className="sa-controls-row">
                <input
                  className="sa-search"
                  placeholder="Search songs…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <label className="sa-check">
                  <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                  Show every song (not just ones with a storyboard, template or pictures)
                </label>
              </div>
              {loading && !topics.length ? (
                <p className="sa-muted">Loading songs…</p>
              ) : visible.length === 0 ? (
                <p className="sa-empty-row">
                  No songs match. Tick “Show every song” to start a storyboard for any synced song.
                </p>
              ) : (
                <div className="sa-table-wrap">
                  <table className="sa-table sa-list">
                    <thead>
                      <tr>
                        <th>Song</th>
                        <th>Storyboard</th>
                        <th>Timings</th>
                        <th aria-label="Open" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => (
                        <tr key={r.topic.slug} className={canOpen(r) ? "" : "is-disabled"}>
                          <td>
                            <div className="sa-topic-title">{r.topic.title || r.topic.slug}</div>
                            <div className="sa-muted sa-topic-slug">{r.topic.slug}</div>
                          </td>
                          <td>
                            {r.board ? (
                              <span className="sa-pill-inline tone-green">saved</span>
                            ) : r.template ? (
                              <span className="sa-pill-inline tone-blue">{r.template.storyboard.title} template</span>
                            ) : (
                              <span className="sa-muted">none yet</span>
                            )}
                            {r.pictureCount > 0 && (
                              <span className="sa-pill-inline tone-blue" style={{ marginLeft: 6 }}>
                                🖼 {r.pictureCount} picture{r.pictureCount === 1 ? "" : "s"}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`sa-pill-inline tone-${r.status.tone}`}>{r.status.label}</span>
                          </td>
                          <td className="sa-row-actions">
                            <button
                              type="button"
                              className="lb-btn lb-btn-sm"
                              disabled={!canOpen(r)}
                              title={
                                r.status.key === "nosync"
                                  ? "Sync this song in the Sing-along studio (or run auto-sync) first"
                                  : ""
                              }
                              onClick={() => openStudio(r.topic.slug)}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default LessonVideoPanel;
